import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'antd/lib/modal/Modal';
import { CloudUploadOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import { PopoverButton } from './PopoverButton';
import { getSignedUrl, podUpload, uploadFilesToS3WithoutMessage } from 'store/commonSlice';

const Header = styled.div`
	display: grid;
	grid-template-columns: 40px 1fr;
	grid-gap: 14px;
`;
const UploadIcon = styled.div`
	width: 40px;
	height: 40px;
	border: 1px solid #c6c6c8;
	border-radius: 999px;
	display: flex;
	justify-content: center;
	align-items: center;
`;
const HeaderText = styled.div`
	display: flex;
	flex-direction: column;
	color: black;
`;

const UploadWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	row-gap: 14px;
	margin: 10px 0;
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
	pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;
const UploadButton = styled.div`
	background-color: transparent;
	border: 1px solid #585858;
	color: #585858;
	padding: 6px 12px;
	cursor: pointer;
`;
const CancelButton = styled.button`
	background-color: transparent;
	border: 1px solid #ff3b30;
	color: #ff3b30;
	width: 110px;
	padding: 2px 0;
	border-radius: 4px;
	cursor: pointer;
`;
const OkButton = styled.button`
	background-color: ${(props) => (props.disabled ? '#E5E5E5' : '#143e60')};
	border: none;
	width: 110px;
	padding: 2px 0;
	color: ${(props) => (props.disabled ? '#999999' : 'white')};
	border-radius: 4px;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	opacity: ${(props) => (props.disabled ? 0.7 : 1)};
`;
const StepWrapper = styled.div`
	padding: 20px;
	min-height: 300px;
`;

const StepNavigation = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: 20px;
`;

const { Dragger } = Upload;

const initialState = {
	open: false,
	file: null,
	currentStep: 1,
	charges: {
		haltCharges: '',
		miscellaneousCharges: '',
		tollCharges: '',
		unloadingCharges: '',
	},
};
export function UploadModal({ consignmentId, disabled, tripId }) {
	const [open, setOpen] = useState(initialState.open);
	const [file, setFile] = useState(initialState.file);
	const [currentStep, setCurrentStep] = useState(initialState.currentStep);
	const [charges, setCharges] = useState(initialState.charges);
	const [fileUploaded, setFileUploaded] = useState(false);
	const dispatch = useDispatch();

	const resetForm = () => {
		setOpen(initialState.open);
		setFile(initialState.file);
		setCurrentStep(initialState.currentStep);
		setCharges(initialState.charges);
		setFileUploaded(false);
	};

	const handleCancel = () => {
		resetForm();
	};

	const handleOk = () => {
		if (!file || !file.uploadData) {
			message.warning('Please upload a file before proceeding');
			return;
		}

		const formattedCharges = Object.entries(charges).reduce((acc, [key, value]) => {
			if (value) {
				acc[key] = Number(value);
			}
			return acc;
		}, {});

		dispatch(uploadFilesToS3WithoutMessage(file.uploadData, file.fileType))
			.then(
				() =>
					new Promise((resolve) => {
						setTimeout(() => {
							resolve();
						}, 2000); // Wait 2 seconds
					})
			)
			.then(() => {
				dispatch(
					podUpload({
						data_key: file.fileName,
						id: consignmentId,
						trip_id: tripId,
						charges: formattedCharges,
					})
				)
					.then((response) => {
						if (response?.data?.status === false) {
							setCurrentStep(2);
						} else {
							resetForm();
							window.location.reload();
						}
					})
					.catch((e) => {
						console.log('e', e);
						setCurrentStep(2);
					});
			})
			.catch((e) => {
				console.log('e', e);
				setCurrentStep(2);
			});
	};

	const handleContinue = () => {
		const formattedCharges = Object.entries(charges).reduce((acc, [key, value]) => {
			if (value) {
				acc[key] = Number(value);
			}
			return acc;
		}, {});
		dispatch(
			podUpload({
				data_key: file.fileName,
				id: consignmentId,
				trip_id: tripId,
				charges: formattedCharges,
				pod_force_upload: true,
			})
		)
			.then(() => {
				resetForm();
				window.location.reload();
			})
			.catch((e) => {
				console.log('e', e);
				setOpen(false);
			});
	};

	const handleFileChange = (info) => {
		if (info.file.size > 5 * 1024 * 1024) {
			setFile(null);
			message.warning('File size must be less than 5 MB');
			return;
		}
		if (info.file.status === 'removed') {
			setFile(null);
			setFileUploaded(false);
			return;
		}
		const data = {
			url_type: 'upload',
			data: {
				entity: 'proof_of_delivery_upload',
				keys: [info.file.name],
			},
		};

		dispatch(getSignedUrl(data)).then((res) => {
			setFile({
				uploadData: {
					data: info.file,
					url: res.data[0].url,
				},
				fileName: res.data[0].filename,
				fileType: info.file.type,
			});
			// Mark file as uploaded to disable upload area
			setFileUploaded(true);
		});
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<StepWrapper>
						<h3>Upload Files</h3>
						<Dragger
							maxCount={1}
							multiple={false}
							name="file"
							accept=".jpeg,.png,.pdf"
							beforeUpload={() => false}
							style={{ backgroundColor: 'transparent' }}
							onChange={handleFileChange}
							showUploadList={file}
							disabled={fileUploaded}
						>
							<UploadWrapper disabled={fileUploaded}>
								<CloudUploadOutlined style={{ fontSize: '24px' }} />
								<p
									style={{
										display: 'flex',
										flexDirection: 'column',
										rowGap: '4px',
									}}
								>
									<span style={{ fontWeight: 'bold' }}>
										{fileUploaded
											? 'File Uploaded'
											: 'Choose a file or drag & drop it here.'}
									</span>
									<span style={{ color: '#99999B' }}>
										JPEG, PNG and PDF formats up to 5 MB
									</span>
								</p>
								<UploadButton>
									{fileUploaded ? 'File Uploaded' : 'Browse Files'}
								</UploadButton>
							</UploadWrapper>
						</Dragger>
						{fileUploaded && (
							<div
								style={{ marginTop: '10px', color: '#52c41a', textAlign: 'center' }}
							>
								File uploaded successfully
							</div>
						)}
						<StepNavigation>
							<CancelButton onClick={handleCancel} type="button">
								Cancel
							</CancelButton>
							<OkButton onClick={handleOk} disabled={!file}>
								Upload Files
							</OkButton>
						</StepNavigation>
					</StepWrapper>
				);
			case 2:
				return (
					<StepWrapper>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								textAlign: 'center',
								minHeight: '220px',
							}}
						>
							<div>
								<CloseCircleOutlined
									style={{ fontSize: '80px', marginBottom: '20px' }}
								/>
							</div>
							<h3 style={{ fontSize: '18px', fontWeight: '400' }}>
								The value of CRN is not matching.
							</h3>
							<p style={{ fontSize: '18px', marginTop: '10px' }}>
								Do you still want to continue?
							</p>
						</div>
						<StepNavigation>
							<CancelButton onClick={handleCancel} type="button">
								Cancel
							</CancelButton>
							<OkButton onClick={handleContinue}>Continue</OkButton>
						</StepNavigation>
					</StepWrapper>
				);
			default:
				return null;
		}
	};

	const getHeaderTitle = () => {
		if (currentStep === 1) {
			return 'Upload Files';
		}
		return 'Confirmation';
	};

	const getHeaderSubtitle = () => {
		if (currentStep === 1) {
			return 'Select and upload the files of your choice';
		}
		return 'Confirm your action';
	};

	return (
		<>
			<PopoverButton textAlign="left" onClick={() => setOpen(true)} disabled={disabled}>
				Upload POD
			</PopoverButton>
			<Modal
				open={open}
				onCancel={handleCancel}
				title={
					<Header>
						<UploadIcon>
							<CloudUploadOutlined style={{ fontSize: '24px' }} />
						</UploadIcon>
						<HeaderText>
							<h4 style={{ fontWeight: 'bold' }}>{getHeaderTitle()}</h4>
							<h4 style={{ color: '#99999B', fontWeight: 'normal' }}>
								{getHeaderSubtitle()}
							</h4>
						</HeaderText>
					</Header>
				}
				footer={null}
				centered
			>
				{renderStepContent()}
			</Modal>
		</>
	);
}

UploadModal.propTypes = {
	consignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	disabled: PropTypes.bool,
	tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
